import { useState, useEffect } from 'react';
import { Plus, Building2, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import { Card, Table, Badge, Button, ConfirmModal, Input } from '@/components/common';
import { tenantService } from '@/services/tenantService';
import { formatDate } from '@/utils/format';
import type { Tenant } from '@/types';

export function TenantsPage() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newTenant, setNewTenant] = useState({
    name: '',
    slug: '',
    domain: '',
  });

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await tenantService.getTenants({ per_page: 50 });
      setTenants(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async () => {
    setLoading(true);
    setError('');
    try {
      await tenantService.createTenant(newTenant);
      setCreateModalOpen(false);
      setNewTenant({ name: '', slug: '', domain: '' });
      fetchTenants();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create tenant');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id: string, isActive: boolean) => {
    try {
      if (isActive) {
        await tenantService.suspendTenant(id);
      } else {
        await tenantService.activateTenant(id);
      }
      fetchTenants();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update tenant status');
    }
  };

  const handleDeleteTenant = async () => {
    if (!tenantToDelete) return;
    setLoading(true);
    try {
      await tenantService.deleteTenant(tenantToDelete);
      setDeleteModalOpen(false);
      setTenantToDelete(null);
      fetchTenants();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete tenant');
    } finally {
      setLoading(false);
    }
  };

  const filteredTenants = tenants.filter(tenant =>
    tenant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tenant.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const columns = [
    {
      key: 'name',
      title: 'Tenant',
      render: (_: any, tenant: Tenant) => (
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-purple-600" />
          <div>
            <p className="font-medium text-secondary-900">{tenant.name}</p>
            <p className="text-xs text-secondary-500">{tenant.slug}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'domain',
      title: 'Domain',
      render: (_: any, tenant: Tenant) => (
        <span className="text-sm text-secondary-600">{tenant.domain || 'No domain'}</span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (_: any, tenant: Tenant) => (
        <Badge variant={tenant.is_active ? 'success' : 'warning'}>
          {tenant.is_active ? 'Active' : 'Suspended'}
        </Badge>
      ),
    },
    {
      key: 'users',
      title: 'Users',
      render: (_: any, tenant: Tenant) => (
        <span className="text-sm text-secondary-600">{tenant.users_count || 0}</span>
      ),
    },
    {
      key: 'created',
      title: 'Created',
      render: (_: any, tenant: Tenant) => (
        <span className="text-sm text-secondary-600">
          {formatDate(tenant.created_at, 'MMM d, yyyy')}
        </span>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: '150px',
      render: (_: any, tenant: Tenant) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleToggleStatus(tenant.id, tenant.is_active);
            }}
            className="p-1.5 hover:bg-secondary-100 rounded transition-colors"
            title={tenant.is_active ? 'Suspend' : 'Activate'}
          >
            {tenant.is_active ? (
              <XCircle className="w-4 h-4 text-orange-600" />
            ) : (
              <CheckCircle className="w-4 h-4 text-green-600" />
            )}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setTenantToDelete(tenant.id);
              setDeleteModalOpen(true);
            }}
            className="p-1.5 hover:bg-red-100 rounded transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Tenants</h1>
          <p className="text-secondary-600 mt-1">Manage multi-tenant organizations</p>
        </div>
        <Button icon={<Plus className="w-4 h-4" />} onClick={() => setCreateModalOpen(true)}>
          New Tenant
        </Button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      <Input
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Search tenants..."
        className="max-w-md"
      />

      <Card padding="none">
        <Table
          data={filteredTenants}
          columns={columns}
          loading={loading}
          emptyMessage="No tenants found"
        />
      </Card>

      {createModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-2xl font-bold mb-4">Create Tenant</h2>
            <div className="space-y-4">
              <Input
                label="Tenant Name"
                value={newTenant.name}
                onChange={(e) => {
                  const name = e.target.value;
                  const slug = name.toLowerCase().replace(/\s+/g, '-');
                  setNewTenant({ ...newTenant, name, slug });
                }}
                placeholder="Acme Corporation"
                required
              />
              <Input
                label="Slug"
                value={newTenant.slug}
                onChange={(e) => setNewTenant({ ...newTenant, slug: e.target.value })}
                placeholder="acme-corp"
                required
              />
              <Input
                label="Domain (optional)"
                value={newTenant.domain}
                onChange={(e) => setNewTenant({ ...newTenant, domain: e.target.value })}
                placeholder="acme.example.com"
              />
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={handleCreateTenant} disabled={loading || !newTenant.name}>
                Create Tenant
              </Button>
              <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setTenantToDelete(null);
        }}
        onConfirm={handleDeleteTenant}
        title="Delete Tenant"
        description="Are you sure you want to delete this tenant? All associated data will be removed."
        confirmText="Delete"
        variant="danger"
        loading={loading}
      />
    </div>
  );
}
